import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Card from '../components/Card.jsx';
import Button from '../components/Button.jsx';
import { GoogleIcon } from '../components/GoogleIcon.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import './auth.css';

export default function Signup() {
  const { signUp, signInWithGoogle, configured } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);

  async function handleGoogle() {
    setFormError('');
    setGoogleSubmitting(true);
    try {
      await signInWithGoogle();
      // Redirect navigates away to Google; session resumes on return.
    } catch (err) {
      setFormError(err.message);
      setGoogleSubmitting(false);
    }
  }

  function validate() {
    const errs = {};
    if (!name.trim()) errs.name = 'Enter your name.';
    if (!email.trim()) errs.email = 'Enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = 'Enter a valid email.';
    if (!password) errs.password = 'Choose a password.';
    else if (password.length < 6) errs.password = 'Password must be at least 6 characters.';
    if (confirmPassword !== password) errs.confirmPassword = 'Passwords do not match.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      await signUp(email.trim(), password, name.trim());
      navigate('/app', { replace: true });
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="aq-public">
      <header className="aq-public-nav">
        <Link to="/" className="aq-public-brand">
          <span className="aq-wordmark">
            Alpha<span className="aq-wordmark-accent">Qap</span>
          </span>
        </Link>
        <nav className="aq-public-nav-actions">
          <Button as={Link} to="/login" variant="ghost" size="sm">
            Log in
          </Button>
        </nav>
      </header>

      <div className="aq-auth-shell">
        <Card className="aq-auth-card">
          <Link to="/" className="aq-auth-back">
            ← Back to home
          </Link>
          <div>
            <h1 className="aq-auth-title heading">Create your account</h1>
            <p className="aq-auth-sub">Start your AlphaQap workspace — technology layer only.</p>
          </div>

          {!configured ? (
            <div className="aq-auth-not-configured">
              Sign-up isn't configured on this deployment yet. The Firebase project vars
              (<code>VITE_FIREBASE_*</code>) haven't been set for this environment — see the
              README for the deploy checklist.
            </div>
          ) : (
            <div className="aq-auth-flow">
              {formError && <div className="aq-auth-alert aq-auth-alert--error">{formError}</div>}

              <Button
                type="button"
                variant="google"
                size="md"
                className="aq-auth-google"
                onClick={handleGoogle}
                disabled={googleSubmitting}
              >
                <GoogleIcon />
                {googleSubmitting ? 'Redirecting to Google…' : 'Continue with Google'}
              </Button>

              <div className="aq-auth-divider"><span>or sign up with email</span></div>

              <form className="aq-auth-form" onSubmit={handleSubmit} noValidate>
              {formError && <div className="aq-auth-alert aq-auth-alert--error">{formError}</div>}

              <div className={`aq-auth-field ${fieldErrors.name ? 'aq-auth-field-error' : ''}`}>
                <label htmlFor="signup-name">Full name</label>
                <input
                  id="signup-name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Adeyemi"
                />
                {fieldErrors.name && (
                  <span className="aq-auth-field-hint aq-auth-field-hint--error">{fieldErrors.name}</span>
                )}
              </div>

              <div className={`aq-auth-field ${fieldErrors.email ? 'aq-auth-field-error' : ''}`}>
                <label htmlFor="signup-email">Email</label>
                <input
                  id="signup-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                />
                {fieldErrors.email && (
                  <span className="aq-auth-field-hint aq-auth-field-hint--error">{fieldErrors.email}</span>
                )}
              </div>

              <div className={`aq-auth-field ${fieldErrors.password ? 'aq-auth-field-error' : ''}`}>
                <label htmlFor="signup-password">Password</label>
                <input
                  id="signup-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                />
                {fieldErrors.password && (
                  <span className="aq-auth-field-hint aq-auth-field-hint--error">{fieldErrors.password}</span>
                )}
              </div>

              <div className={`aq-auth-field ${fieldErrors.confirmPassword ? 'aq-auth-field-error' : ''}`}>
                <label htmlFor="signup-confirm-password">Confirm password</label>
                <input
                  id="signup-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                />
                {fieldErrors.confirmPassword && (
                  <span className="aq-auth-field-hint aq-auth-field-hint--error">
                    {fieldErrors.confirmPassword}
                  </span>
                )}
              </div>

              <Button type="submit" variant="primary" className="aq-auth-submit" disabled={submitting}>
                {submitting ? 'Creating account…' : 'Create account'}
              </Button>
            </form>
            </div>
          )}

          <div className="aq-auth-links">
            <span>
              Already have an account? <Link to="/login">Log in</Link>
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
}
